// File: frontend/src/components/Calendar/NewAppointmentModal.js
// VERSION v1.40 (2025-11-06) - "Phone Mask Hotfix"
// Description:
// - [ЗАДАЧА 42 / v1.40] РЕШЕНИЕ: Добавлена маска телефона в "Create New Appointment".
//   1. Импортирован 'react-input-mask'.
//   2. Обычный <input> для 'client_phone' заменен на <InputMask>.
//   3. 'handleSubmit' теперь очищает маску телефона перед отправкой в API.
//   4. Добавлена валидация в 'validateForm' для неполного номера.
// - (Сохранено из v1.39.1) Все исправления "Calendly-Style" сохранены.

import React, { useState, useEffect } from 'react';
import calendarService from '../../services/calendarService';
import LoadingSpinner from '../LoadingSpinner';
import './NewAppointmentModal.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format as formatTz, toZonedTime } from 'date-fns-tz';
import toast from 'react-hot-toast';
// --- [ИСПРАВЛЕНИЕ v1.40] ---
import InputMask from 'react-input-mask';
// --- [КОНЕЦ ИСПРАВЛЕНИЯ v1.40] ---

// Хелпер для форматирования даты в YYYY-MM-DD (без изменений)
const getLocalDateStr = (date) => {
    try {
        return formatTz(date, 'yyyy-MM-dd', { timeZone: 'UTC' });
    } catch (e) {
        console.error("Error formatting date to yyyy-MM-dd:", e);
        const d = new Date(date);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
};


const NewAppointmentModal = ({ isOpen, onClose, onAppointmentCreated }) => {
    // Состояние компонента
    const [attorneys, setAttorneys] = useState([]);
    const [loadingAttorneys, setLoadingAttorneys] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // --- [ИЗМЕНЕНИЯ v1.39 - УПРАВЛЕНИЕ ДАТОЙ/СЛОТАМИ] ---
    const [selectedDate, setSelectedDate] = useState(null); // Для подсветки в DatePicker
    const [selectedDayStr, setSelectedDayStr] = useState(null); // 'YYYY-MM-DD' для API
    const [selectedSlot, setSelectedSlot] = useState(null); // Выбранный ISO-строка слота
    const [availableSlots, setAvailableSlots] = useState([]); // Массив объектов Date
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    // --- [КОНЕЦ v1.39] ---

    const [formData, setFormData] = useState({
        attorney_user_id: '',
        client_email: '',
        client_name: '',
        client_phone: '', 
        datetime_str: '', // Будет установлено через handleSlotClick
        topic: '',
        duration_minutes: 30,
    });
    const [errors, setErrors] = useState({});

    // --- [НОВЫЙ ХЕЛПЕР v1.39] ---
    // (без изменений)
    const formatDateTimeForBackend = (date) => {
        if (!date) return '';
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
            const zonedDate = toZonedTime(date, timeZone);
            return formatTz(zonedDate, "yyyy-MM-dd'T'HH:mm:ssXXXXX", { timeZone });
        } catch (error) {
            console.error("Error formatting date (formatDateTimeForBackend):", error);
            toast.error("Error formatting time.");
            return date.toISOString(); // Fallback
        }
    };
    // --- [КОНЕЦ v1.39] ---

    // Эффект для загрузки юристов и сброса формы (без изменений)
    useEffect(() => {
        if (isOpen) {
            loadAttorneys();
            resetForm();
        }
    }, [isOpen]);

    // Функция сброса состояния формы
    const resetForm = () => {
        setFormData({
            attorney_user_id: '',
            client_email: '',
            client_name: '',
            client_phone: '', 
            datetime_str: '',
            topic: '',
            duration_minutes: 30,
        });
        setErrors({});
        // --- [ИЗМЕНЕНИЯ v1.39] ---
        setSelectedDate(null);
        setAvailableSlots([]); 
        setIsLoadingSlots(false); 
        setSelectedDayStr(null); 
        setSelectedSlot(null); // Сброс выбранного слота
        // --- [КОНЕЦ v1.39] ---
    };

    // Загрузка списка юристов (без изменений)
    const loadAttorneys = async () => {
        try {
            setLoadingAttorneys(true);
            const data = await calendarService.getAvailableAttorneys();
            setAttorneys(data || []);
             if (data && data.length > 0 && !formData.attorney_user_id) {
                 setFormData(prev => ({ ...prev, attorney_user_id: data[0].id }));
             }
        } catch (error) {
            console.error('Failed to load attorneys:', error);
            setAttorneys([]);
             toast.error(`Failed to load attorneys: ${error.message || 'Unknown error'}`);
        } finally {
            setLoadingAttorneys(false);
        }
    };

    // Загружает свободные слоты при изменении юриста ИЛИ выбранного дня (без изменений)
    useEffect(() => {
        const fetchFreeSlots = async () => {
            if (!formData.attorney_user_id || !selectedDayStr) {
                setAvailableSlots([]);
                return;
            }

            console.log(`v1.39: Запрос свободных слотов для юриста ${formData.attorney_user_id} на дату ${selectedDayStr}`);
            setIsLoadingSlots(true);
            try {
                const slotsData = await calendarService.getFreeSlots(
                    formData.attorney_user_id,
                    selectedDayStr
                );
                
                const slotDates = slotsData.map(isoString => new Date(isoString));
                setAvailableSlots(slotDates);
                
                if (slotDates.length === 0) {
                    toast.error("No available slots for this attorney on this day.", { duration: 2000 });
                }

            } catch (error) {
                console.error('Failed to load free slots:', error);
                toast.error(`Failed to load free slots: ${error.message || 'Unknown error'}`);
                setAvailableSlots([]);
            } finally {
                setIsLoadingSlots(false);
            }
        };

        fetchFreeSlots();
    }, [formData.attorney_user_id, selectedDayStr]);

    // --- [ИСПРАВЛЕНИЕ v1.40] ---
    // Функция валидации (добавлена проверка телефона)
    const validateForm = () => {
        const newErrors = {};
        if (!formData.attorney_user_id) newErrors.attorney_user_id = 'Please select an attorney';
        if (!formData.client_email) {
            newErrors.client_email = 'Client email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email)) {
            newErrors.client_email = 'Invalid email format';
        }
        if (!formData.client_name) newErrors.client_name = 'Client name is required';
        
        // --- [ИСПРАВЛЕНИЕ v1.40] ---
        // Добавляем валидацию для *непустого* телефона
        if (formData.client_phone) {
            const cleanPhone = formData.client_phone.replace(/[^\d]/g, '');
            // Если он не пустой, он должен быть полным (11 цифр: 1 + 10)
            if (cleanPhone.length > 0 && cleanPhone.length < 11) { 
                newErrors.client_phone = 'Phone number must be 10 digits (e.g., +1 212-555-1234)';
            }
        }
        // --- [КОНЕЦ ИСПРАВЛЕНИЯ v1.40] ---

        if (!formData.datetime_str) newErrors.datetime_str = 'Please select a date and time slot'; // Обновлен текст
        if (!formData.topic) newErrors.topic = 'Topic is required';
        if (formData.duration_minutes < 15 || formData.duration_minutes > 480) newErrors.duration_minutes = 'Duration must be between 15 and 480 minutes';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    // --- [КОНЕЦ ИСПРАВЛЕНИЯ v1.40] ---

    // Обработчик изменений для input/select (без изменений)
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };


    // --- [БЛОК ИЗМЕНЕН v1.39 - "Стиль Calendly"] ---
    // (без изменений)
    const handleDateChange = (date) => {
        setSelectedDate(date); 
        setSelectedSlot(null); 
        setFormData(prev => ({ ...prev, datetime_str: '' })); 

        let newDayStr = null;
        if (date) {
            newDayStr = getLocalDateStr(date); 
        }

        if (newDayStr !== selectedDayStr) {
            console.log("v1.39: День изменился, запускаем поиск слотов для:", newDayStr);
            setSelectedDayStr(newDayStr);
        }

        if (errors.datetime_str) {
            setErrors((prev) => ({ ...prev, datetime_str: '' }));
        }
    };
    // --- [КОНЕЦ v1.39] ---

    // --- [НОВЫЙ ОБРАБОТЧИК v1.39] ---
    // (без изменений)
    const handleSlotClick = (slotDate) => {
        const isoString = slotDate.toISOString();
        setSelectedSlot(isoString); 

        const backendDateTimeStr = formatDateTimeForBackend(slotDate);
        
        console.log("v1.39: Слот выбран. ISO:", isoString, "Backend str:", backendDateTimeStr);

        setFormData((prev) => ({
            ...prev,
            datetime_str: backendDateTimeStr,
        }));

        if (errors.datetime_str) {
            setErrors((prev) => ({ ...prev, datetime_str: '' }));
        }
    };
    // --- [КОНЕЦ v1.39] ---

    // Обработчик отправки формы
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return; 

        setIsSubmitting(true);
        setErrors({}); 
        console.log("Submitting formData (v1.39):", formData); 

        // --- [ИСПРАВЛЕНИЕ v1.40] ---
        // Очищаем маску телефона перед отправкой в API
        const cleanPhone = formData.client_phone ? formData.client_phone.replace(/[^\d+]/g, '') : null;
        const payload = {
            ...formData,
            client_phone: cleanPhone // Отправляем очищенный номер
        };
        console.log("Submitting CLEANED payload (v1.40):", payload);
        // --- [КОНЕЦ ИСПРАВЛЕНИЯ v1.40] ---

        try {
            // --- [ИСПРАВЛЕНИЕ v1.40] ---
            // Отправляем 'payload', а не 'formData'
            await calendarService.createAppointment(payload);
            // --- [КОНЕЦ ИСПРАВЛЕНИЯ v1.40] ---
            
            // (toast.success закомментирован в v1.39.1)
            
            resetForm(); 
            if (onAppointmentCreated) onAppointmentCreated(); 
            onClose(); 
        } catch (error) {
            console.error('Error creating appointment:', error);
            const errorMessage = error?.response?.data?.detail || error.message || 'Failed to create appointment';
            setErrors({ submit: errorMessage }); 
            toast.error(`Error: ${errorMessage}`); 
        } finally {
            setIsSubmitting(false);
        }
    };

    // Рендер модального окна
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Заголовок */}
                <div className="modal-header">
                    <h2>Create New Appointment</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                
                {/* Форма */}
                <form onSubmit={handleSubmit} className="appointment-form">
                    {errors.submit && (<div className="error-message general-error">{errors.submit}</div>)}

                    {/* Поля Клиента и Темы (перенесены вверх) */}
                    <div className="form-group-grid">
                        {/* Имя клиента */}
                        <div className="form-group">
                            <label htmlFor="client_name">Client Name *</label>
                            <input
                                type="text" id="client_name" name="client_name"
                                value={formData.client_name}
                                onChange={handleInputChange}
                                placeholder="Enter client name"
                                className={`form-input ${errors.client_name ? 'input-error' : ''}`}
                               />
                            {errors.client_name && (<span className="error-text">{errors.client_name}</span>)}
                        </div>

                        {/* Email клиента */}
                        <div className="form-group">
                            <label htmlFor="client_email">Client Email *</label>
                            <input
                                type="email" id="client_email" name="client_email"
                                value={formData.client_email}
                                onChange={handleInputChange}
                                placeholder="client@example.com"
                                className={`form-input ${errors.client_email ? 'input-error' : ''}`}
                               />
                            {errors.client_email && (<span className="error-text">{errors.client_email}</span>)}
                        </div>

                         {/* --- [ИСПРАВЛЕНИЕ v1.40] --- */}
                         {/* Телефон клиента (Заменен на InputMask) */}
                         <div className="form-group">
                            <label htmlFor="client_phone">Client Phone</label>
                            <InputMask
                                mask="+1 (999) 999-9999"
                                maskChar={null} // Не показывать "_"
                                id="client_phone"
                                name="client_phone"
                                value={formData.client_phone}
                                onChange={handleInputChange}
                            >
                                {(inputProps) => (
                                    <input
                                        {...inputProps}
                                        type="tel"
                                        placeholder="+1 (___) ___-____"
                                        className={`form-input ${errors.client_phone ? 'input-error' : ''}`}
                                    />
                                )}
                            </InputMask>
                            {errors.client_phone && (<span className="error-text">{errors.client_phone}</span>)}
                        </div>
                        {/* --- [КОНЕЦ ИСПРАВЛЕНИЯ v1.40] --- */}

                         {/* Тема встречи */}
                        <div className="form-group">
                            <label htmlFor="topic">Topic *</label>
                            <input
                                type="text" id="topic" name="topic"
                                value={formData.topic}
                                onChange={handleInputChange}
                                placeholder="e.g., Initial Consultation on Will"
                                className={`form-input ${errors.topic ? 'input-error' : ''}`}
                            />
                            {errors.topic && (<span className="error-text">{errors.topic}</span>)}
                        </div>
                    </div> {/* Конец .form-group-grid */}


                    {/* --- [БЛОК ИЗМЕНЕН v1.39] --- */}
                    {/* Разделитель */}
                    <hr className="form-divider" />

                    {/* Выбор юриста */}
                    <div className="form-group">
                        <label htmlFor="attorney_user_id">Attorney *</label>
                        <select
                            id="attorney_user_id" name="attorney_user_id"
                            value={formData.attorney_user_id}
                            onChange={handleInputChange}
                            disabled={loadingAttorneys}
                            className={`form-input ${errors.attorney_user_id ? 'input-error' : ''}`}
                        >
                            <option value="">{loadingAttorneys ? 'Loading attorneys...' : '-- Select Attorney --'}</option>
                            {attorneys.map((attorney) => (
                                <option key={attorney.id} value={attorney.id}>
                                    {attorney.first_name} {attorney.last_name} ({attorney.email})
                                </option>
                            ))}
                        </select>
                        {errors.attorney_user_id && (<span className="error-text">{errors.attorney_user_id}</span>)}
                    </div>

                    {/* Выбор даты и времени (СТИЛЬ CALENDLY) */}
                    <div className="form-group">
                        <label>Date & Time *</label>
                        
                        <div className="calendly-style-picker">
                            {/* Часть 1: Календарь */}
                            <div className="calendly-datepicker-wrapper">
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateChange} // v1.39
                                    disabled={!formData.attorney_user_id}
                                    minDate={new Date()} // Нельзя выбрать прошлое
                                    inline // ВАЖНО: Календарь всегда открыт
                                />
                            </div>
                            
                            {/* Часть 2: Слоты */}
                            <div className="calendly-slots-wrapper">
                                {isLoadingSlots && <LoadingSpinner />}
                                
                                {!isLoadingSlots && selectedDayStr && availableSlots.length > 0 && (
                                    <div className="slots-list">
                                        {availableSlots.map(slotDate => {
                                            const iso = slotDate.toISOString();
                                            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                            return (
                                                <button
                                                    type="button"
                                                    key={iso}
                                                    className={`btn btn-slot ${selectedSlot === iso ? 'btn-primary' : 'btn-secondary'}`}
                                                    onClick={() => handleSlotClick(slotDate)}
                                                >
                                                    {formatTz(slotDate, 'h:mm aa', { timeZone })}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {!isLoadingSlots && selectedDayStr && availableSlots.length === 0 && (
                                    <div className="slots-message">
                                        No available slots on this day.
                                    </div>
                                )}

                                {!isLoadingSlots && !selectedDayStr && (
                                    <div className="slots-message">
                                        {formData.attorney_user_id ? "Please select a date" : "Please select an attorney first"}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Общая ошибка для этого поля */}
                        {errors.datetime_str && (
                            <span className="error-text">{errors.datetime_str}</span>
                        )}
                    </div>
                    {/* --- [КОНЕЦ v1.39] --- */}

                    {/* Длительность (скрыта) */}
                     <div className="form-group" style={{ display: 'none' }}>
                         <label htmlFor="duration_minutes">Duration (minutes) *</label>
                         <input
                             type="number" id="duration_minutes" name="duration_minutes"
                             value={formData.duration_minutes}
                             onChange={handleInputChange}
                             readOnly
                             className={`form-input ${errors.duration_minutes ? 'input-error' : ''}`}
                            />
                         {errors.duration_minutes && (<span className="error-text">{errors.duration_minutes}</span>)}
                     </div>

                    {/* Кнопки */}
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary bg-secondary text-secondary-foreground hover:bg-muted"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary bg-primary text-primary-foreground hover:bg-primary/90"
                            disabled={isSubmitting || isLoadingSlots || !formData.datetime_str} // Блокируем, если не выбран слот
                        >
                            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Create Appointment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewAppointmentModal;